import React, { useRef } from 'react';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import camelcaseKeys from 'camelcase-keys';
import fetch from 'cross-fetch';
import { v4 as uuidv4 } from 'uuid';

import { documentApi } from '../api';
import useCompanyFeatures from '../components/Authorizer/useCompanyFeatures';
import { FeatureTypes } from '../constants/featureTypes';
import { Statuses } from '../constants/statuses';
import RequestToScanModal
  from '../pages/V2InsuredProfilePage/components/RecordDetailsContainer/components/RecordDetails/components/DocumentRequirements/components/DocumentsTableContainer/components/DocumentsTable/components/AiDocumentAnalysis/components/RequestToScanModal';
import { createErrorToast, createSuccessToast, createToast } from '../redux/modules/app';
import { createDocumentForRecord, prepareDocument, updateDocument } from '../redux/modules/documents';
import { hideModal, showModal } from '../redux/modules/modal';
import { modifyArrayItemAt, removeArrayItemAt } from '../utils/arrayUtil';
import { getCurrentTimestamp, isPast } from '../utils/dateUtil';
import { queryClient } from '../utils/queryClientUtil';

import insuredKeys from './models/insured/keys';
import recordKeys from './models/record/keys';
import { camelCaseApiResponse, optimisticUpdateActiveRecords, setOverallStatusAndRecordStatusAsFetching } from './util';

/* DOC UPLOAD HELPERS
========================================================= */
export const uploadDocument = async (
  recordId,
  insuranceDocumentTypeId,
  file,
  dispatch,
  status = null
) => {
  const fileUniqueId = uuidv4();

  const document = await dispatch(createDocumentForRecord(recordId, {
    doc_type_id: insuranceDocumentTypeId,
    original_name: file.name,
    file_unique_id: fileUniqueId,
    size: file.size
  }));

  const response = await fetch(document.upload_url, {
    method: 'PUT',
    body: file
  });

  if (response.status < 200 || response.status > 300 || !response.ok) {
    throw new Error(`Failed uploading the document. Status: ${response.status}, Message: ${response.statusText}`);
  }

  const updateData = status ? { url: response.url, status } : { url: response.url };

  return dispatch(updateDocument(document.id, updateData));
};

export const usePrepareDocument = async (
  recordId,
  insuranceDocumentTypeId,
  file,
  dispatch,
  fileUniqueId = null
) => {
  const document = await dispatch(createDocumentForRecord(recordId, {
    doc_type_id: insuranceDocumentTypeId,
    original_name: file.name,
    size: file.size,
    file_unique_id: fileUniqueId,
    update_status: false
  }));

  const response = await fetch(document.upload_url, {
    method: 'PUT',
    body: file
  });

  if (response.status < 200 || response.status > 300 || !response.ok) {
    throw new Error(`Failed uploading the document. Status: ${response.status}, Message: ${response.statusText}`);
  }

  // // upload first three pages to Textract S3
  // // wrap the reader in a Promise to ensure we wait for the readAsArrayBuffer to complete
  // const readFileAsArrayBuffer = fileToUpload => new Promise((resolve, reject) => {
  //   const reader = new FileReader();
  //
  //   reader.onload = () => {
  //     resolve(new Uint8Array(reader.result)); // resolves with the PDF bytes
  //   };
  //
  //   reader.onerror = reject;
  //
  //   reader.readAsArrayBuffer(fileToUpload);
  // });
  //
  // try {
  //   const pdfBytes = await readFileAsArrayBuffer(file);
  //
  //   const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
  //   const totalPages = pdfDoc.getPageCount();
  //
  //   if (totalPages > 3) {
  //     const newPdfDoc = await PDFDocument.create();
  //
  //     const [page1, page2, page3] = await newPdfDoc.copyPages(pdfDoc, [0, 1, 2]);
  //     newPdfDoc.addPage(page1);
  //     newPdfDoc.addPage(page2);
  //     newPdfDoc.addPage(page3);
  //
  //     const extractedPdfBytes = await newPdfDoc.save();
  //
  //     // upload only the first three pages to textract url
  //     await fetch(document.textract_url, {
  //       method: 'PUT',
  //       body: extractedPdfBytes
  //     });
  //   } else {
  //     // upload the original file to textract url
  //     await fetch(document.textract_url, {
  //       method: 'PUT',
  //       body: file
  //     });
  //   }
  // } catch (e) {
  //   console.log('There was a problem with parsing the document.  Using the original file instead to prepare document for scanning');
  //
  //   await fetch(document.textract_url, {
  //     method: 'PUT',
  //     body: file
  //   });
  //
  //   if (e.message.includes('encrypted')) {
  //     console.log('PDF is encrypted.');
  //   }
  // }

  return dispatch(prepareDocument(document.id, { url: response.url }));
};

const uploadDoc = async ({
  dispatch,
  file,
  recordId,
  insuredId,
  insuranceDocumentTypeId,
  inputRef
}) => {
  dispatch(createToast({
    title: 'Uploading document',
    description: file.size > 10 * 1024 * 1024 ? 'Files larger than 10MB may take longer to process.' : file.name,
    duration: 120000,
    status: 'general'
  }));

  let document;

  try {
    document = await uploadDocument(recordId, insuranceDocumentTypeId, file, dispatch);
    await Promise.all([
      queryClient.invalidateQueries(recordKeys.recordDocumentRequirements(recordId)),
      queryClient.invalidateQueries(recordKeys.recordDocuments(recordId))
    ]);

    queryClient.invalidateQueries(insuredKeys.insuredOverallStatus(insuredId));
    queryClient.invalidateQueries(insuredKeys.insuredActiveRecords(insuredId));

    dispatch(createSuccessToast({
      title: 'Document Uploaded Successfully!',
      duration: 5000
    }));

    setOverallStatusAndRecordStatusAsFetching(insuredId, recordId);
  } catch (error) {
    dispatch(createErrorToast({
      title: 'Something went wrong with your upload',
      description: error.message,
      duration: 12000,
      status: 'error'
    }));

    queryClient.invalidateQueries(recordKeys.recordDocumentRequirements(recordId));
    queryClient.invalidateQueries(recordKeys.recordDocuments(recordId));
    queryClient.invalidateQueries(insuredKeys.insuredOverallStatus(insuredId));
    queryClient.invalidateQueries(insuredKeys.insuredActiveRecords(insuredId));
  } finally {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  return document;
};

export const useDocUploader = ({ insuranceDocumentTypeId, insuredId, recordId }) => {
  const dispatch = useDispatch();
  const inputRef = useRef();

  const { companyHasFeature: aiDocumentAnalysisEnabled } = useCompanyFeatures(FeatureTypes.AiDocumentAnalysis);
  const { companyHasFeature: aiTierOneEnabled } = useCompanyFeatures(FeatureTypes.AiTierOne);
  const { companyHasFeature: aiTierTwoEnabled } = useCompanyFeatures(FeatureTypes.AiTierTwo);
  const { companyHasFeature: aiTierThreeEnabled } = useCompanyFeatures(FeatureTypes.AiTierThree);

  const aiEnabled = aiDocumentAnalysisEnabled || aiTierOneEnabled || aiTierTwoEnabled || aiTierThreeEnabled;

  const handleChange = async e => {
    const file = e.target.files[0];
    if (!file) return;

    // check file size for a 50MB limit
    if (file.size > 50 * 1024 * 1024) {
      dispatch(createErrorToast({
        title: 'Document too large',
        description: 'Files larger than 50MB are not permitted.',
        duration: 10000
      }));

      return;
    }

    const document = await uploadDoc({
      dispatch,
      file,
      recordId,
      insuredId,
      insuranceDocumentTypeId,
      inputRef
    });

    if (aiEnabled && document) {
      const onClose = () => {
        dispatch(hideModal());
      };

      dispatch(showModal(
        () => <RequestToScanModal document={document} file={file} recordId={recordId} onClose={onClose} />,
        { size: 'lg', center: false }
      ));
    }
  };

  const triggerClick = () => {
    inputRef.current?.click();
  };

  return {
    inputRef,
    handleChange,
    triggerClick
  };
};

/* QUERIES
========================================================= */
/* MUTATIONS
========================================================= */
export const useUpdateDocument = ({ docId, insuredId, recordId }) => {
  const dispatch = useDispatch();
  const documents = recordKeys.activeRecordDocuments(recordId);
  const documentRequirements = recordKeys.recordDocumentRequirements(recordId);
  const overallStatus = insuredKeys.insuredOverallStatus(insuredId);
  const activeRecordStatuses = insuredKeys.insuredActiveRecords(insuredId);

  return useMutation(
    data => camelCaseApiResponse(() => documentApi.updateDocument(docId, data)),
    {
      onMutate: async updateData => {
        await queryClient.cancelQueries(documents);
        await queryClient.cancelQueries(documentRequirements);
        await queryClient.cancelQueries(overallStatus);
        await queryClient.cancelQueries(activeRecordStatuses);

        const previousDocs = queryClient.getQueryData(documents);
        const previousDocumentRequirements = queryClient.getQueryData(documentRequirements);
        const previousOverallStatus = queryClient.getQueryData(overallStatus);
        const previousRecordStatuses = queryClient.getQueryData(activeRecordStatuses);

        const updateDocs = modifyArrayItemAt(
          previousDocs,
          'id',
          docId,
          doc => ({ ...doc, ...camelcaseKeys(updateData) })
        );

        queryClient.setQueryData(documents, updateDocs);

        dispatch(createSuccessToast({ title: 'Document Updated!' }));

        return { previousDocs, previousOverallStatus, previousRecordStatuses, previousDocumentRequirements };
      },
      onError: (error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your document was not updated.',
          error
        }));

        queryClient.setQueryData(documents, context.previousDocs);
        queryClient.setQueryData(documentRequirements, context.previousDocumentRequirements);
        queryClient.setQueryData(overallStatus, context.previousOverallStatus);
        queryClient.setQueryData(activeRecordStatuses, context.previousRecordStatuses);
      },
      onSuccess: async () => {
        Promise.all([
          queryClient.invalidateQueries(documents),
          queryClient.invalidateQueries(documentRequirements),
          queryClient.invalidateQueries(overallStatus),
          queryClient.invalidateQueries(activeRecordStatuses)
        ]);
      }
    }
  );
};

export const useUpdateDocumentName = ({ docId, recordId }) => {
  const dispatch = useDispatch();
  const activeDocumentsKey = recordKeys.activeRecordDocuments(recordId);

  return useMutation(
    data => camelCaseApiResponse(() => documentApi.updateDocumentName(docId, data)),
    {
      onMutate: async updateData => {
        await queryClient.cancelQueries(activeDocumentsKey);

        const previousDocs = queryClient.getQueryData(activeDocumentsKey);

        const updateDocs = modifyArrayItemAt(
          previousDocs,
          'id',
          docId,
          doc => ({ ...doc, displayName: updateData.name })
        );

        queryClient.setQueryData(activeDocumentsKey, updateDocs);

        dispatch(createSuccessToast({ title: 'Document Name Updated!' }));

        return { previousDocs };
      },
      onError: (error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your document name was not updated.',
          error
        }));

        queryClient.setQueryData(activeDocumentsKey, context.previousDocs);
      },
      onSuccess: async () => {
        Promise.all([
          queryClient.invalidateQueries(activeDocumentsKey)
        ]);
      }
    }
  );
};

export const useArchiveDocument = ({ docId, insuredId, docTypeId, recordId }) => {
  const dispatch = useDispatch();
  const activeRecordDocumentsKey = recordKeys.activeRecordDocuments(recordId);
  const archivedRecordDocumentsKey = recordKeys.archivedRecordDocuments(recordId);
  const documentRequirements = recordKeys.recordDocumentRequirements(recordId);
  const policyRequirements = recordKeys.recordPolicyRequirements(recordId);
  const itemRequirements = recordKeys.recordItemRequirements(recordId);
  const overallStatus = insuredKeys.insuredOverallStatus(insuredId);
  const activeRecordStatuses = insuredKeys.insuredActiveRecords(insuredId);

  return useMutation(
    () => camelCaseApiResponse(() => documentApi.archiveDocument(docId)),
    {
      onMutate: async () => {
        await queryClient.cancelQueries(activeRecordDocumentsKey);
        await queryClient.cancelQueries(archivedRecordDocumentsKey);
        await queryClient.cancelQueries(documentRequirements);
        await queryClient.cancelQueries(overallStatus);
        await queryClient.cancelQueries(activeRecordStatuses);

        const previousActiveDocs = queryClient.getQueryData(activeRecordDocumentsKey) ?? [];
        const previousArchivedDocs = queryClient.getQueryData(archivedRecordDocumentsKey) ?? [];
        const previousDocumentRequirements = queryClient.getQueryData(documentRequirements);
        const previousOverallStatus = queryClient.getQueryData(overallStatus);
        const previousRecordStatuses = queryClient.getQueryData(activeRecordStatuses);

        const selectedDoc = [...previousActiveDocs, ...previousArchivedDocs].find(doc => doc.id === docId);
        const selectedDocTypeId = selectedDoc.insuranceDocumentTypeId;
        const currentDocTypeRequirement = previousDocumentRequirements.find(docReq => docReq.insuranceDocumentTypeId === selectedDoc.insuranceDocumentTypeId);
        const currentDocTypeStatus = currentDocTypeRequirement?.status;

        // optimistic update active/archived documents
        const timestamp = getCurrentTimestamp();

        const newActiveDocs = removeArrayItemAt(
          previousActiveDocs,
          'id',
          docId
        );

        const newArchivedDocs = [
          ...previousArchivedDocs,
          { ...selectedDoc, archivedAt: timestamp, status: Statuses.Archived, expiresAt: null, policies: [] }
        ];

        queryClient.setQueryData(activeRecordDocumentsKey, newActiveDocs);
        queryClient.setQueryData(archivedRecordDocumentsKey, newArchivedDocs);

        // optimistic update doc req status
        if (currentDocTypeStatus && currentDocTypeStatus !== Statuses.Waived) {
          let calcStatus = Statuses.Pending;

          const activeDocsOfType = newActiveDocs.filter(doc => doc.insuranceDocumentTypeId === selectedDocTypeId);

          if (!activeDocsOfType.length) {
            calcStatus = Statuses.NotReceived;
          } else {
            const expiredDocStillExists = activeDocsOfType.find(doc => {
              if (isPast(doc.expiresAt)) {
                return true;
              }

              return doc.policies?.length && doc.policies.find(pol => isPast(pol.expiresAt));
            });

            if (expiredDocStillExists) {
              calcStatus = Statuses.Expired;
            } else if (currentDocTypeStatus === Statuses.Expired) {
              calcStatus = Statuses.Pending;
            }
          }

          const newDocReqs = previousDocumentRequirements.map(docReq => {
            if (docReq.insuranceDocumentTypeId === selectedDocTypeId) {
              return { ...docReq, status: calcStatus };
            }

            return docReq;
          });

          queryClient.setQueryData(documentRequirements, newDocReqs);
        }

        // optimistic updates on active records
        optimisticUpdateActiveRecords({
          recordId,
          activeRecordsKey: activeRecordStatuses,
          docReqKey: documentRequirements,
          polReqKey: policyRequirements,
          itemReqKey: itemRequirements
        });

        dispatch(createSuccessToast({ title: 'Document Archived!' }));

        return { previousActiveDocs, previousArchivedDocs, previousOverallStatus, previousRecordStatuses, previousDocumentRequirements };
      },
      onError: (error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your document was not archived.',
          error
        }));

        queryClient.setQueryData(activeRecordDocumentsKey, context.previousActiveDocs);
        queryClient.setQueryData(archivedRecordDocumentsKey, context.previousArchivedDocs);
        queryClient.setQueryData(documentRequirements, context.previousDocumentRequirements);
        queryClient.setQueryData(overallStatus, context.previousOverallStatus);
        queryClient.setQueryData(activeRecordStatuses, context.previousRecordStatuses);

        Promise.all([
          queryClient.invalidateQueries(activeRecordDocumentsKey),
          queryClient.invalidateQueries(documentRequirements),
          queryClient.invalidateQueries(overallStatus),
          queryClient.invalidateQueries(activeRecordStatuses)
        ]);
      },
      onSuccess: async () => {}
    }
  );
};

export const useReactivateDocument = ({ docId, insuredId, recordId, docTypeId }) => {
  const dispatch = useDispatch();
  const activeDocumentsKey = recordKeys.activeRecordDocuments(recordId);
  const archivedDocumentsKey = recordKeys.archivedRecordDocuments(recordId);
  const policyRequirementsKey = recordKeys.recordPolicyRequirements(recordId);
  const documentRequirementsKey = recordKeys.recordDocumentRequirements(recordId);
  const itemRequirements = recordKeys.recordItemRequirements(recordId);
  const overallStatus = insuredKeys.insuredOverallStatus(insuredId);
  const activeRecordStatuses = insuredKeys.insuredActiveRecords(insuredId);

  return useMutation(
    () => camelCaseApiResponse(() => documentApi.reactivateDocument(docId)),
    {
      onMutate: async () => {
        await queryClient.cancelQueries(activeDocumentsKey);
        await queryClient.cancelQueries(archivedDocumentsKey);
        await queryClient.cancelQueries(documentRequirementsKey);
        await queryClient.cancelQueries(overallStatus);
        await queryClient.cancelQueries(activeRecordStatuses);

        const previousActiveDocs = queryClient.getQueryData(activeDocumentsKey) ?? [];
        const previousArchivedDocs = queryClient.getQueryData(archivedDocumentsKey) ?? [];
        const previousDocumentRequirements = queryClient.getQueryData(documentRequirementsKey);
        const previousOverallStatus = queryClient.getQueryData(overallStatus);
        const previousRecordStatuses = queryClient.getQueryData(activeRecordStatuses);

        const selectedDoc = [...previousActiveDocs, ...previousArchivedDocs].find(doc => doc.id === docId);
        const currentDocTypeRequirement = previousDocumentRequirements.find(docReq => docReq.insuranceDocumentTypeId === selectedDoc.insuranceDocumentTypeId);
        const currentDocTypeStatus = currentDocTypeRequirement?.status;

        const newActiveDocs = [
          ...previousActiveDocs,
          { ...selectedDoc, status: Statuses.Active, archivedAt: null }
        ];

        const newArchivedDocs = removeArrayItemAt(
          previousArchivedDocs,
          'id',
          docId
        );
        queryClient.setQueryData(activeDocumentsKey, newActiveDocs);
        queryClient.setQueryData(archivedDocumentsKey, newArchivedDocs);

        // optimistic update document requirements
        if (currentDocTypeStatus && ![Statuses.Waived, Statuses.Expired].includes(currentDocTypeStatus)) {
          // theres no expired archived docs, so we always set to Pending unless the status is already waived or expired
          const newDocReqs = modifyArrayItemAt(
            previousDocumentRequirements,
            'insuranceDocumentTypeId',
            docTypeId,
            docReq => ({ ...docReq, status: Statuses.Pending })
          );

          queryClient.setQueryData(documentRequirementsKey, newDocReqs);
        }

        // optimistic update active records
        optimisticUpdateActiveRecords({
          recordId,
          polReqKey: policyRequirementsKey,
          docReqKey: documentRequirementsKey,
          activeRecordsKey: activeRecordStatuses,
          itemReqKey: itemRequirements
        });

        dispatch(createSuccessToast({ title: 'Document Reactivated' }));

        return { previousActiveDocs, previousArchivedDocs, previousDocumentRequirements, previousOverallStatus, previousRecordStatuses };
      },
      onError: (error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your document was not reactivated.',
          error
        }));

        queryClient.setQueryData(activeDocumentsKey, context.previousActiveDocs);
        queryClient.setQueryData(archivedDocumentsKey, context.previousArchivedDocs);
        queryClient.setQueryData(documentRequirementsKey, context.previousDocumentRequirements);
        queryClient.setQueryData(overallStatus, context.previousOverallStatus);
        queryClient.setQueryData(activeRecordStatuses, context.previousRecordStatuses);

        Promise.all([
          queryClient.invalidateQueries(activeDocumentsKey),
          queryClient.invalidateQueries(archivedDocumentsKey),
          queryClient.invalidateQueries(documentRequirementsKey),
          queryClient.invalidateQueries(overallStatus),
          queryClient.invalidateQueries(activeRecordStatuses)
        ]);
      },
      onSuccess: async () => {}
    }
  );
};

export const useDeleteDocument = ({ docId, insuredId, docTypeId, recordId }) => {
  const dispatch = useDispatch();
  const activeRecordDocumentsKey = recordKeys.activeRecordDocuments(recordId);
  const archivedRecordDocumentsKey = recordKeys.archivedRecordDocuments(recordId);
  const policyRequirementsKey = recordKeys.recordPolicyRequirements(recordId);
  const documentRequirementsKey = recordKeys.recordDocumentRequirements(recordId);
  const itemRequirements = recordKeys.recordItemRequirements(recordId);
  const overallStatus = insuredKeys.insuredOverallStatus(insuredId);
  const activeRecordStatuses = insuredKeys.insuredActiveRecords(insuredId);

  return useMutation(
    () => camelCaseApiResponse(() => documentApi.deleteDocument(docId)),
    {
      onMutate: async () => {
        await queryClient.cancelQueries(activeRecordDocumentsKey);
        await queryClient.cancelQueries(archivedRecordDocumentsKey);
        await queryClient.cancelQueries(documentRequirementsKey);
        await queryClient.cancelQueries(overallStatus);
        await queryClient.cancelQueries(activeRecordStatuses);

        const previousActiveDocs = queryClient.getQueryData(activeRecordDocumentsKey) ?? [];
        const previousArchivedDocs = queryClient.getQueryData(archivedRecordDocumentsKey) ?? [];
        const previousDocumentRequirements = queryClient.getQueryData(documentRequirementsKey);
        const previousOverallStatus = queryClient.getQueryData(overallStatus);
        const previousRecordStatuses = queryClient.getQueryData(activeRecordStatuses);

        const selectedDoc = [...previousActiveDocs, ...previousArchivedDocs].find(doc => doc.id === docId);
        const selectedDocIsActive = !selectedDoc.archivedAt && !selectedDoc.deletedAt;
        const selectedDocTypeId = selectedDoc.insuranceDocumentTypeId;
        const currentDocTypeRequirement = previousDocumentRequirements.find(docReq => docReq.insuranceDocumentTypeId === selectedDoc.insuranceDocumentTypeId);
        const currentDocTypeStatus = currentDocTypeRequirement?.status;

        // optimistic update active/archived documents
        const newActiveDocs = removeArrayItemAt(
          previousActiveDocs,
          'id',
          docId
        );

        const newArchivedDocs = removeArrayItemAt(
          previousArchivedDocs,
          'id',
          docId
        );

        queryClient.setQueryData(activeRecordDocumentsKey, newActiveDocs);
        queryClient.setQueryData(archivedRecordDocumentsKey, newArchivedDocs);

        // optimistic update doc req status if doc was active and current status is not waived
        if (selectedDocIsActive && currentDocTypeStatus && currentDocTypeStatus !== Statuses.Waived) {
          let calcStatus = Statuses.Pending;

          const activeDocsOfType = newActiveDocs.filter(doc => doc.insuranceDocumentTypeId === selectedDocTypeId);

          if (!activeDocsOfType.length) {
            calcStatus = Statuses.NotReceived;
          } else {
            const expiredDocStillExists = activeDocsOfType.find(doc => {
              if (isPast(doc.expiresAt)) {
                return true;
              }

              return doc.policies?.length && doc.policies.find(pol => isPast(pol.expiresAt));
            });

            if (expiredDocStillExists) {
              calcStatus = Statuses.Expired;
            } else if (currentDocTypeStatus === Statuses.Expired) {
              calcStatus = Statuses.Pending;
            }
          }

          const newDocReqs = previousDocumentRequirements.map(docReq => {
            if (docReq.insuranceDocumentTypeId === selectedDocTypeId) {
              return { ...docReq, status: calcStatus };
            }

            return docReq;
          });

          queryClient.setQueryData(documentRequirementsKey, newDocReqs);
        }

        // optimistic updates on active records
        optimisticUpdateActiveRecords({
          recordId,
          activeRecordsKey: activeRecordStatuses,
          docReqKey: documentRequirementsKey,
          polReqKey: policyRequirementsKey,
          itemReqKey: itemRequirements
        });

        dispatch(createSuccessToast({ title: 'Document Deleted!' }));

        return { previousActiveDocs, previousArchivedDocs, previousDocumentRequirements, previousOverallStatus, previousRecordStatuses };
      },
      onError: (error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your document was not deleted.',
          error
        }));

        queryClient.setQueryData(activeRecordDocumentsKey, context.previousActiveDocs);
        queryClient.setQueryData(archivedRecordDocumentsKey, context.previousArchivedDocs);
        queryClient.setQueryData(documentRequirementsKey, context.previousDocumentRequirements);
        queryClient.setQueryData(overallStatus, context.previousOverallStatus);
        queryClient.setQueryData(activeRecordStatuses, context.previousRecordStatuses);

        Promise.all([
          queryClient.invalidateQueries(activeRecordDocumentsKey),
          queryClient.invalidateQueries(documentRequirementsKey),
          queryClient.invalidateQueries(overallStatus),
          queryClient.invalidateQueries(activeRecordStatuses)
        ]);
      },
      onSuccess: async () => {}
    }
  );
};

export const useUpdateAssociatedPolicies = ({ recordId, insuredId }) => {
  const dispatch = useDispatch();
  const documents = recordKeys.activeRecordDocuments(recordId);
  const activePoliciesKey = recordKeys.activeRecordPolicies(recordId);
  const policyRequirements = recordKeys.recordPolicyRequirements(recordId);
  const documentRequirements = recordKeys.recordDocumentRequirements(recordId);
  const overallStatus = insuredKeys.insuredOverallStatus(insuredId);
  const activeRecordStatuses = insuredKeys.insuredActiveRecords(insuredId);

  const generateModifiedPolicyArray = (policies, policyIds) => policies.filter(p => policyIds.includes(p.id));

  return useMutation(
    ({
      document,
      policyIds
    }) => {
      const { id: docId } = document;
      return camelCaseApiResponse(() => documentApi.updateAssociatedPolicies(docId, policyIds));
    },
    {
      onMutate: async ({ document, policyIds }) => {
        const { id: docId, policies, insuranceDocumentTypeId: docTypeId } = document;

        await queryClient.cancelQueries(documents);
        await queryClient.cancelQueries(activePoliciesKey);
        await queryClient.cancelQueries(documentRequirements);
        await queryClient.cancelQueries(policyRequirements);
        await queryClient.cancelQueries(overallStatus);
        await queryClient.cancelQueries(activeRecordStatuses);

        const previousDocs = queryClient.getQueryData(documents);
        const previousActivePolicies = queryClient.getQueryData(activePoliciesKey);
        const previousDocumentRequirements = queryClient.getQueryData(documentRequirements);
        const previousPolicyRequirements = queryClient.getQueryData(policyRequirements);
        const previousOverallStatus = queryClient.getQueryData(overallStatus);
        const previousRecordStatuses = queryClient.getQueryData(activeRecordStatuses);

        dispatch(createToast({ title: 'Updating document policy links' }));

        const modifiedPolicyArray = generateModifiedPolicyArray(previousActivePolicies, policyIds);

        const updateData = modifyArrayItemAt(
          previousDocs,
          'id',
          docId,
          doc => ({ ...doc, policies: modifiedPolicyArray })
        );

        queryClient.setQueryData(documents, updateData);

        setOverallStatusAndRecordStatusAsFetching(insuredId, recordId);

        return { previousDocs, previousDocumentRequirements, previousPolicyRequirements, previousOverallStatus, previousRecordStatuses };
      },
      onError: (error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your policy was not linked to this document.',
          error
        }));

        queryClient.setQueryData(documents, context.previousDocs);
        queryClient.setQueryData(documentRequirements, context.previousDocumentRequirements);
        queryClient.setQueryData(documents, context.previousPolicyRequirements);
        queryClient.setQueryData(overallStatus, context.previousOverallStatus);
        queryClient.setQueryData(activeRecordStatuses, context.previousRecordStatuses);
      },
      onSuccess: async () => {
        Promise.all([
          queryClient.invalidateQueries(documents),
          queryClient.invalidateQueries(documentRequirements),
          queryClient.invalidateQueries(policyRequirements),
          queryClient.invalidateQueries(overallStatus),
          queryClient.invalidateQueries(activeRecordStatuses)
        ]);

        dispatch(createSuccessToast({ title: 'Document links updated!', duration: 4000 }));
      }
    }
  );
};

// A custom expiration changes the document's effective expiration, which the backend folds into the
// document requirement + overall/record statuses. Invalidate that whole family so the UI reflects the
// recomputed status instead of the stale cached value (queries default to refetchOnMount: false).

